/**
 * useCmsNav — navigation tree, fresher than the bundle.
 *
 * Snapshot seeds state; `/api/cms/public/nav` replaces it on mount so a menu
 * edit in the CMS shows without a rebuild.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsNav, type NavItem } from '@/content/cms/nav.generated'
import { previewTokenParam } from '@/utils/previewToken'
import { DEFAULT_LOCALE, type Locale } from '@/utils/localePath'
import { useCmsData } from '@/cms/CmsDataContext'

interface NavResp {
  nav?: NavItem[]
}

export function useCmsNav(): NavItem[] {
  const { i18n } = useTranslation()
  const locale = (i18n.language || DEFAULT_LOCALE) as Locale
  const cmsData = useCmsData()
  const initial = cmsData?.nav ?? cmsNav
  const [nav, setNav] = useState<NavItem[]>(initial)

  useEffect(() => {
    if (cmsData?.nav) return
    const controller = new AbortController()
    let cancelled = false

    void fetch(
      `/api/cms/public/nav${previewTokenParam()}`,
      { signal: controller.signal, credentials: 'same-origin' },
    )
      .then((r) => (r.ok ? (r.json() as Promise<NavResp>) : null))
      .then((j) => {
        if (cancelled) return
        if (!j?.nav?.length) return
        setNav(j.nav)
      })
      .catch(() => {
        // Network error / abort — keep the snapshot seed.
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [locale, cmsData?.nav])

  return nav
}
